import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { logger } from '@/utils/logger'

/**
 * Get the default view for a specific board
 * @param {Object} params - Request parameters
 * @param {String} params.boardId - The board ID to get the default view for
 * @returns {Promise} - The default view data
 */
export async function getDefaultView({ boardId }) {
  try {
    const response = await axios.get(`${await API_URL()}/default-views/${boardId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (response.status === 200) {
      return response.data
    } else throw new Error(response.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

/**
 * Get all user default views for a specific board
 * @param {Object} params - Request parameters
 * @param {String} params.boardId - The board ID to get all default views for
 * @returns {Promise} - All user default views data
 */
export async function getAllDefaultViews({ boardId }) {
  try {
    const response = await axios.get(`${await API_URL()}/default-views/${boardId}/all`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (response.status === 200) {
      return response.data
    } else throw new Error(response.statusText)
  } catch (error) {
    logger.error(error.message)
    return []
  }
}

/**
 * Set a default view for a specific user
 * @param {Object} params - Request parameters
 * @param {String} params.boardId - The board ID to set the default view for
 * @param {String} params.viewId - The view ID to set as default
 * @param {String} params.userId - The user ID to set the default view for
 * @returns {Promise} - The result of the operation
 */
export async function setDefaultViewForUser({ boardId, viewId, userId }) {
  try {
    const response = await axios.post(
      `${await API_URL()}/default-views/${boardId}/user/${userId}`,
      {
        data: { viewId }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (response.status === 200) {
      return response.data
    } else throw new Error(response.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

/**
 * Remove the default view for a specific user
 * @param {Object} params - Request parameters
 * @param {String} params.boardId - The board ID
 * @param {String} params.userId - The user ID to remove the default view for
 * @returns {Promise} - The result of the operation
 */
export async function removeDefaultViewForUser({ boardId, userId }) {
  try {
    const response = await axios.delete(
      `${await API_URL()}/default-views/${boardId}/user/${userId}`,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (response.status === 200) {
      return response.data
    } else throw new Error(response.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}
